import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import Toast from '../components/Toast';
import SearchableSelect from '../components/SearchableSelect';

const ACTION_LABELS = {
  login: 'Signed in',
  login_failed: 'Failed login',
  logout: 'Signed out',
  logout_everywhere: 'Signed out all sessions',
  process_submission: 'Processed submission',
  unprocess_submission: 'Reopened submission',
  submit_payroll: 'Submitted payroll',
  submit_changes: 'Submitted payroll changes',
  create_pending_change: 'Created change',
  add_pending_change: 'Added pending change',
  delete_pending_change: 'Deleted change',
  DELETE_PENDING_CHANGE: 'Deleted change',
  undo_pending_change: 'Removed pending change',
  update_salary: 'Updated salary',
  view_employee: 'Viewed employee',
  view_employees: 'Viewed employees',
  create_employee: 'Added employee',
  update_employee: 'Updated employee',
  upload_document: 'Uploaded document',
  upload_documents: 'Uploaded documents',
  export_csv: 'Exported CSV',
  export_pdf: 'Exported PDF',
  view_as_client: 'Viewed as client',
  switch_company: 'Switched company',
  '2fa_enabled': 'Enabled 2FA',
  '2fa_disabled': 'Disabled 2FA',
  enable_2fa: 'Enabled 2FA',
  disable_2fa: 'Disabled 2FA',
  password_reset: 'Reset password',
  password_change: 'Changed password',
  password_changed: 'Changed password',
  invite_client: 'Invited client',
  invite_user: 'Invited user',
  invite_admin: 'Invited team member',
  resend_invite: 'Resent invite',
  delete_company: 'Deleted company',
  delete_user: 'Deleted user',
  update_role: 'Changed user role',
  update_pay_day: 'Updated pay day',
  google_drive_connected: 'Connected Google Drive',
  google_drive_disconnected: 'Disconnected Google Drive',
};

const ACTION_CATEGORY = {
  // Auth
  login: 'auth', logout: 'auth', logout_everywhere: 'auth',
  password_reset: 'auth', password_change: 'auth', password_changed: 'auth',
  '2fa_enabled': 'auth', '2fa_disabled': 'auth', enable_2fa: 'auth', disable_2fa: 'auth',
  // Failures pop in rose
  login_failed: 'failure',
  // Submissions
  process_submission: 'submission', unprocess_submission: 'submission',
  submit_payroll: 'submission', submit_changes: 'submission',
  create_pending_change: 'submission', add_pending_change: 'submission',
  delete_pending_change: 'submission', DELETE_PENDING_CHANGE: 'submission',
  undo_pending_change: 'submission',
  // Admin (default)
};

function categoryOf(action) { return ACTION_CATEGORY[action] || 'admin'; }

const CATEGORY = {
  submission: { label: 'Submissions', tone: 'emerald', pill: 'bg-emerald-100 text-emerald-800 ring-emerald-200', dot: 'bg-emerald-500' },
  admin:      { label: 'Admin',       tone: 'brand',   pill: 'bg-brand-100 text-brand-700 ring-brand-200',     dot: 'bg-brand-500' },
  email:      { label: 'Emails',      tone: 'sky',     pill: 'bg-sky-100 text-sky-800 ring-sky-200',           dot: 'bg-sky-500' },
  auth:       { label: 'Auth',        tone: 'amber',   pill: 'bg-amber-100 text-amber-800 ring-amber-200',     dot: 'bg-amber-500' },
  failure:    { label: 'Failures',    tone: 'rose',    pill: 'bg-rose-100 text-rose-800 ring-rose-200',        dot: 'bg-rose-500' },
};

function actionLabel(action) {
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatMetadata(meta) {
  if (!meta) return '';
  if (typeof meta !== 'object') return String(meta).slice(0, 100);
  return Object.entries(meta)
    .filter(([k]) => !['ip', 'ipAddress'].includes(k))
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(', ')
    .slice(0, 120);
}

const PRESETS = [
  { key: 'all',   label: 'All time' },
  { key: '7d',    label: 'Last 7 days' },
  { key: '30d',   label: 'Last 30 days' },
  { key: 'custom', label: 'Custom range' },
];

function presetToRange(key) {
  if (key === 'all' || key === 'custom') return { date_from: '', date_to: '' };
  const days = key === '7d' ? 7 : 30;
  const from = new Date(); from.setDate(from.getDate() - days);
  const to = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  return { date_from: iso(from), date_to: iso(to) };
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [customRange, setCustomRange] = useState({ date_from: '', date_to: '' });
  const [categoryFilter, setCategoryFilter] = useState(null); // client-side toggle

  const loadCompanies = useCallback(async () => {
    try { const res = await api.get('/admin/companies'); setCompanies(res.data); }
    catch { /* ignore */ }
  }, []);

  const effectiveRange = datePreset === 'custom' ? customRange : presetToRange(datePreset);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (companyFilter) params.company_id = companyFilter;
      if (actionFilter) params.action = actionFilter;
      if (effectiveRange.date_from) params.date_from = effectiveRange.date_from;
      if (effectiveRange.date_to) params.date_to = effectiveRange.date_to;
      const res = await api.get('/admin/audit-logs', { params });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.totalCount || 0);
    } catch (err) {
      setToast({ message: 'Failed to load audit logs', type: 'error' });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, companyFilter, actionFilter, datePreset, customRange.date_from, customRange.date_to]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [companyFilter, actionFilter, datePreset, customRange.date_from, customRange.date_to]);

  // Counts per category from the current page
  const categoryCounts = useMemo(() => {
    const c = { submission: 0, admin: 0, email: 0, auth: 0, failure: 0 };
    logs.forEach((l) => { const cat = categoryOf(l.action); if (c[cat] !== undefined) c[cat] += 1; });
    return c;
  }, [logs]);

  // Client-side filter for search + category chip (applied to current page)
  const visibleLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (categoryFilter && categoryOf(l.action) !== categoryFilter) return false;
      if (!q) return true;
      return (
        (l.user_name || '').toLowerCase().includes(q) ||
        (l.user_email || '').toLowerCase().includes(q) ||
        actionLabel(l.action).toLowerCase().includes(q) ||
        (l.company_name || '').toLowerCase().includes(q) ||
        formatMetadata(l.metadata).toLowerCase().includes(q)
      );
    });
  }, [logs, search, categoryFilter]);

  const pillSel = 'text-[12.5px] rounded-md ring-1 ring-gray-200 bg-white text-gray-700 px-2.5 py-1.5 focus:ring-brand-400';

  return (
    <div className="max-w-6xl mx-auto">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <header className="mb-6">
        <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Audit Log</h1>
        <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2">Every action performed across the system, with category-coloured tags so failed logins and role changes stand out.</p>
      </header>

      {/* Filter strip */}
      <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-4 py-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
            </span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user, action, company, or metadata…"
              className="w-full pl-8 pr-3 py-1.5 text-[13.5px] rounded-md ring-1 ring-gray-200 focus:ring-brand-400 focus:outline-none" />
          </div>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className={pillSel}>
            <option value="">All actions</option>
            {Object.entries(ACTION_LABELS).map(([k, label]) => (<option key={k} value={k}>{label}</option>))}
          </select>
          <div className="min-w-[200px]">
            <SearchableSelect value={companyFilter} onChange={(v) => setCompanyFilter(v || '')}
              placeholder="All companies" options={companies.map((c) => ({ value: c.id, label: c.company_name }))} />
          </div>
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} className={pillSel}>
            {PRESETS.map((p) => (<option key={p.key} value={p.key}>{p.label}</option>))}
          </select>
          {datePreset === 'custom' && (
            <>
              <input type="date" value={customRange.date_from} onChange={(e) => setCustomRange((r) => ({ ...r, date_from: e.target.value }))} className={pillSel} />
              <span className="text-gray-400 text-[12px]">to</span>
              <input type="date" value={customRange.date_to} onChange={(e) => setCustomRange((r) => ({ ...r, date_to: e.target.value }))} className={pillSel} />
            </>
          )}
        </div>
      </div>

      {/* Category quick-filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {['submission', 'admin', 'email', 'auth', 'failure'].map((key) => {
          const count = categoryCounts[key];
          if (!count) return null;
          const active = categoryFilter === key;
          const cat = CATEGORY[key];
          return (
            <button key={key} onClick={() => setCategoryFilter(active ? null : key)}
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[12.5px] font-medium ring-1 transition-shadow ${cat.pill} ${active ? 'shadow-sm ring-2' : 'opacity-90 hover:opacity-100'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} />
              {cat.label}
              <span className="text-[11px] tabular opacity-70">({count})</span>
            </button>
          );
        })}
        {categoryFilter && (
          <button onClick={() => setCategoryFilter(null)} className="text-[12px] text-gray-500 hover:text-gray-700">Clear category</button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr className="text-left text-[11.5px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2 font-bold">When</th>
                <th className="px-4 py-2 font-bold">User</th>
                <th className="px-4 py-2 font-bold">Action</th>
                <th className="px-4 py-2 font-bold">Company</th>
                <th className="px-4 py-2 font-bold">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-500">Loading…</td></tr>
              ) : visibleLogs.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-500">No audit events match the current filters.</td></tr>
              ) : visibleLogs.map((log) => {
                const cat = CATEGORY[categoryOf(log.action)] || CATEGORY.admin;
                return (
                  <tr key={log.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-4 py-2.5 text-[12px] text-gray-500 whitespace-nowrap tabular">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-2.5 text-[13px] text-gray-900">{log.user_name || log.user_email || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded font-semibold text-[12px] ring-1 ${cat.pill}`}>{actionLabel(log.action)}</span>
                    </td>
                    <td className="px-4 py-2.5 text-[13px] text-gray-700">{log.company_name || '—'}</td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-500 max-w-xs truncate">{formatMetadata(log.metadata) || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination footer baked into the card */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-3">
          <span className="text-[12px] text-gray-500 tabular">
            Page <strong className="text-gray-700">{page}</strong> of <strong className="text-gray-700">{totalPages || 1}</strong> · {totalCount.toLocaleString('en-GB')} events
          </span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="px-3 py-1.5 text-[12.5px] font-medium rounded-md bg-white ring-1 ring-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="px-3 py-1.5 text-[12.5px] font-medium rounded-md bg-white ring-1 ring-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
